import { Component, OnInit } from '@angular/core'
import { Router, ActivatedRoute, RoutesRecognized } from '@angular/router'
import { Location } from '@angular/common'
import { BaseComponent } from '../../components/base.component'
import { CustomerActions } from '../../actions/customer-actions'
import { sortedCustomers, isAgencySignup, isSignup, hasSetupAccountProfile, fullDayWithTime, dateRoundOff } from '../../utils'
import { earliestUnfinishedRoute } from '../../logic/customer-signup'
import { AccountActions } from '../../actions/account-actions'
import capitalize from 'lodash-es/capitalize'
import get from 'lodash-es/get'
import { topAlert } from '../../logic/customer-alerts'
import sweetAlert from '../../bmAlert'
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed'
import { filter, pairwise, startWith } from 'rxjs/operators'
import { combineLatest } from 'rxjs'
import { CustomersNavQueries, CustomerData, User, Customer, Account } from './customers-nav.queries'
import { QueryRef } from 'apollo-angular'
import pluralize from 'pluralize'
import { EditspaceService } from 'src/services/editspace.service'

@Component({
  selector: 'customers-nav',
  templateUrl: './customers-nav.html'
})
export class CustomersNav extends BaseComponent implements OnInit {
  user: User
  account: Account;
  customers: Customer[] = [];
  myCustomers: Customer[] = []
  expiredCustomers: Customer[] = []
  hiddenCustomers: Customer[] = []
  searchString: string = ''
  searchCriteria: string = 'businessName'
  expiredExpanded: boolean = false
  hiddenExpanded: boolean = false
  selectedCustomerExpired: boolean = false
  isAgencySignup: boolean = false
  isSignup: boolean = false
  hasSetupAccountProfile: boolean = false
  capitalize: typeof capitalize = capitalize;
  topAlert: typeof topAlert = topAlert;
  queryRef: QueryRef<CustomerData>
  loading: boolean
  customerId: number
  isEditspace = false

  constructor(private route: ActivatedRoute,
    private accountActions: AccountActions,
    private customerActions: CustomerActions,
    public location: Location,
    private router: Router,
    private queries: CustomersNavQueries,
    private editspaceService: EditspaceService) {
    super()
  }

  ngOnInit(): void {
    this.isEditspace = this.editspaceService.isEditspace()
    const userObs = this.queries.user().valueChanges.subscribe(({data}) => {
      if (data && data.currentUser) {
        this.user = data.currentUser
        this.account = this.user.account
        this.isAgencySignup = isAgencySignup(this.account)
        this.isSignup = isSignup(this.account)
        this.hasSetupAccountProfile = hasSetupAccountProfile(this.account)
        if (this.isWhiteLabel()) {
          this.searchCriteria = 'displayName'
        }

        this.queryRef = this.queries.customers({withSignupFields: this.isAgencySignup})
        const obs = combineLatest(
          this.route.params.pipe(startWith({})),
          this.queryRef.valueChanges.pipe(startWith({data: null, loading: true, stale: true}))
        ).subscribe(( [params, {data, loading, stale}] ) => {
          this.customerId = +params['customerId']

          this.loading = loading && stale
          if (data) {
            this.customers = data.customers.entries
            this.expiredCustomers = sortedCustomers(this.customers.filter(c => c.state === 'expired'))
            const currentCustomers = this.customers.filter(c => !this.expiredCustomers.map(ec => ec.id).includes(c.id))
            this.hiddenCustomers = sortedCustomers(currentCustomers.filter(c => this.user.hiddenCustomers.includes(c.id)))
            this.myCustomers = sortedCustomers(currentCustomers.filter(c => !this.hiddenCustomers.map(hc => hc.id).includes(c.id)))
          }

          if (!this.expiredExpanded) {
            this.expiredExpanded = this.isExpired(this.customerId)
          }
          if (!this.hiddenExpanded) {
            this.hiddenExpanded = this.isHidden(this.customerId)
          }
          if (this.customerId && this.customers.length > 0) {
            this.scrollSelectedCustomerIntoView()
          }
        })
        this.observers.push(obs)
      }
    })
    this.observers.push(userObs)

    this.router.events
      .pipe(filter((e: any) => e instanceof RoutesRecognized),
        pairwise()
      )
  }

  get overviewVisible(): boolean {
    return this.customers.length > 1 && this.user.canUpdateCustomer
  }

  get canToggleExpiredExpanded(): boolean {
    return !(this.expiredExpanded && this.isExpired(this.customerId))
  }

  get canToggleHiddenExpanded(): boolean {
    return !(this.hiddenExpanded && this.isHidden(this.customerId))
  }

  subTitleSubscriptionTypeText(customer: Customer): string {
    if(this.isAgencySignup) {
      return capitalize(this.clientSignupStatus(customer))
    } else if (this.user?.canUpdateCustomer) {
      if (customer.subscription?.state === 'active') {
        return customer.multipass ? 'Any length' : `${customer.subscription.plan.words}-words`
      } else {
        return this.customerStateString(customer)
      }
    }
  }

  customerName(customer: Customer): string {
    return this.isWhiteLabel() ? customer.displayName : customer.businessName
  }

  isExpired(customerId: number): boolean {
    return this.expiredCustomers.map(c => c.id).includes(customerId)
  }

  isHidden(customerId: number): boolean {
    return this.hiddenCustomers.map(c => c.id).includes(customerId)
  }

  isWhiteLabel(): boolean {
    return this.user?.role === "white_label"
  }

  onToggleExpiredExpanded(): void {
    if (this.canToggleExpiredExpanded) {
      this.expiredExpanded = !this.expiredExpanded
    }
  }

  onToggleHiddenExpanded(): void {
    if (this.canToggleHiddenExpanded) {
      this.hiddenExpanded = !this.hiddenExpanded
    }
  }

  scrollSelectedCustomerIntoView(): void {
    setTimeout(() => {
      const leftNav = document.getElementById('left-nav') || document.getElementById('mat-sidenav')
      const selectedCustomerButton = document.getElementById(`customer-nav-button-${this.customerId}`)
      if (leftNav && selectedCustomerButton) {
        scrollIntoViewIfNeeded(selectedCustomerButton, {boundary: leftNav, easing: 'easeIn', duration: 220, offset: {bottom: 50}})
      }
    }, 0)
  }

  computeRoute(customer: Customer): any {
    if (this.isAgencySignup) {
      return earliestUnfinishedRoute(this.account, customer)
    } else if (this.onRequestEditsPage) {
      return ['/dashboard', customer.id]
    } else if (this.onRequestsPage) { // switching between vid and non-vid subscriptions doesn't work without this
      return ['/dashboard', customer.id, 'topics']
    } else {
      return this.location.path().split('?')[0].split('/').map((val, index) => index === 2 ? customer.id : val)
    }
  }

  isCustomerSelected(customerId: number): boolean {
    if (customerId && this.isAgencySignup) {
      // params exists on child of child relative to the component where customers-nav lives (NewAccountPage)
      return customerId === <number>(+get(this.route, ['firstChild', 'firstChild', 'snapshot', 'params', 'customerId'], null))
    } else {
      return this.customerId === customerId
    }
  }

  isValetCustomer(customer: Customer): boolean {
    return customer.subscription?.selectedProducts?.some(p => p.code === "valet")
  }

  getAlertTooltipCopy(alert: any): string {
    if (alert?.area === 'topics' && alert?.level === 'problem') {
      return 'Add at least one active request to receive submissions.'
    }
  }

  creditsTooltipCopy(customer: Customer): string {
    if (customer.subscription?.scheduledToCancelAt) {
      return `This subscription is scheduled to cancel at ${fullDayWithTime(customer.subscription.scheduledToCancelAt, true)}`
    } else if (customer.subscription?.scheduledToPauseAt) {
      return `This subscription is scheduled to pause at ${fullDayWithTime(customer.subscription.scheduledToPauseAt, true)}`
    } else if (['active', 'paused'].includes(customer?.subscription?.state)) {
      const creditsMonthly = pluralize(`${customer.subscription.quantity} credit`, customer.subscription.quantity)
      let renewalDay = dateRoundOff(customer.subscription.nextCreditsIssuedAt)

      return `${creditsMonthly} will be added with your next invoice on ${fullDayWithTime(renewalDay, true)}.`
    }
  }

  // ###########################################################################
  // ####                     Related to Agency Signup                      ####
  // ###########################################################################

  get onAgencySignupHome(): boolean {
    let path = this.location.path()
    return !!(path.match(/\/account\/signup(\?|$)/) ||
            path.match(/\/account\/signup\/clients\/home(\?|$)/))
  }

  get onActivationPage(): boolean {
    let path = this.location.path()
    return !!(path.match(/\/account\/signup\/activation(\?|$)/))
  }

  get isOnNewClientPage(): boolean {
    return !!(this.location.path().match(/\/account\/signup\/clients\/new(\?|$)/))
  }

  get onRequestEditsPage(): boolean {
    return !!(this.location.path().match(/\/edit_requests\//))
  }

  get onRequestsPage(): boolean {
    return !!(this.location.path().match(/dashboard\/\d+\/topics/))
  }

  get addAnotherClientButtonSelected(): boolean {
    return !!this.isOnNewClientPage
  }

  clientSignupStatus(customer: Customer): string {
    return (customer && customer.plan) ? 'awaiting activation' : 'needs plan'
  }

  customerStateString(c: Customer): string {
    if (c.state === 'pending') {
      return 'Incomplete'
    } else if (c.state === 'paused') {
      return 'Skipped'
    } else {
      return capitalize(c.state)
    }
  }

  hoveringId: number;
  hover(customerId: number): void {
    if (this.hoveringId !== customerId) this.hoveringId = customerId
  }
  isHovering(customerId: number): boolean {
    return this.hoveringId === customerId
  }

  showCheck(customer: Customer): boolean {
    if (customer) return customer.plan && !this.isHovering(customer.id)
  }

  removeCustomer(customerId: number, event: MouseEvent): void {
    event.stopPropagation()
    if (customerId) {
      sweetAlert.confirm({text: "Are you sure you want to remove this client?"}).then(confirm => {
        if (confirm) {
          this.customerActions.removeCustomer(customerId)
          .then(() => {
            this.queryRef.refetch().then(() => {
              if (this.isCustomerSelected(customerId)) {
                this.router.navigate(['/account', 'signup'])
              } else if (this.customers.length === 0 && this.onActivationPage) {
                this.router.navigate(['/account', 'signup', 'clients', 'home'])
              }
            })
          })
        }
      })
    }
  }

  canActivateAccount(): boolean {
    return this.customers && this.customers.filter(c => c.plan).length > 0
  }

  trackById(index: number, thing: { id: number }): number {
    return thing.id
  }
}
